/**
 * Submits email signup forms to Kit (ConvertKit) using the public V3 form API,
 * with optional Shopify customer newsletter sync.
 */
class KitNewsletterSignup extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.target);
    const emailInput = form.querySelector('input[type="email"]');

    if (!(emailInput instanceof HTMLInputElement)) return;

    const email = emailInput.value.trim();
    if (!email) {
      this.#showError(form, this.dataset.errorRequired || 'Enter your email address.');
      return;
    }

    const formId = this.dataset.kitFormId;
    const apiKey = this.dataset.kitApiKey;

    if (!formId || !apiKey) {
      this.#showError(form, this.dataset.errorConfig || 'Newsletter signup is not configured yet.');
      return;
    }

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    this.#clearMessages(form);

    try {
      await this.#subscribeToKit(formId, apiKey, email);

      if (this.dataset.syncShopify === 'true') {
        await this.#syncShopifyCustomer(form, email);
      }

      this.#showSuccess(form);
      form.reset();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : this.dataset.errorGeneric || 'Something went wrong. Please try again.';
      this.#showError(form, message);
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }
  };

  /**
   * @param {string} formId
   * @param {string} apiKey
   * @param {string} email
   */
  async #subscribeToKit(formId, apiKey, email) {
    const response = await fetch(`https://api.convertkit.com/v3/forms/${formId}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        api_key: apiKey,
        email,
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message =
        typeof data?.message === 'string'
          ? data.message
          : typeof data?.error === 'string'
            ? data.error
            : this.dataset.errorGeneric || 'Subscription failed. Please try again.';
      throw new Error(message);
    }

    return data;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} email
   */
  async #syncShopifyCustomer(form, email) {
    const action = form.getAttribute('action') || '/contact#contact_form';
    const formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[email]', email);
    formData.append('contact[tags]', 'newsletter');

    const tokenInput = form.querySelector('input[name="authenticity_token"]');
    if (tokenInput instanceof HTMLInputElement && tokenInput.value) {
      formData.append('authenticity_token', tokenInput.value);
    }

    await fetch(action, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #showError(form, message) {
    this.#clearMessages(form);

    const errorEl = form.querySelector('[data-kit-newsletter-error]');
    if (!(errorEl instanceof HTMLElement)) return;

    errorEl.hidden = false;
    const textEl = errorEl.querySelector('[data-kit-newsletter-error-text]');
    if (textEl) textEl.textContent = message;
    errorEl.focus();
  }

  /**
   * @param {HTMLFormElement} form
   */
  #showSuccess(form) {
    this.#clearMessages(form);

    const successEl = form.querySelector('[data-kit-newsletter-success]');
    if (!(successEl instanceof HTMLElement)) return;

    successEl.hidden = false;
    successEl.focus();
  }

  /**
   * @param {HTMLFormElement} form
   */
  #clearMessages(form) {
    for (const el of form.querySelectorAll('[data-kit-newsletter-error], [data-kit-newsletter-success]')) {
      if (el instanceof HTMLElement) {
        el.hidden = true;
      }
    }
  }
}

if (!customElements.get('kit-newsletter-signup')) {
  customElements.define('kit-newsletter-signup', KitNewsletterSignup);
}
